import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFileUrl } from "@/lib/utils";
import { useToast } from "@/features/toast";
import { useFilePickerStore } from "@/features/file-picker";
import { useFileUpload, validateFile } from "@/features/file-upload";
import {
  getModuleChatConfig,
  useActiveModule,
  type ModuleRouteHandle,
} from "@/modules";
import useAuthStore from "@/store/use-auth-store";
import { useAuthGateStore } from "@/features/auth-gate";
import { useChatDock } from "@/features/chat-dock/hooks/use-chat-dock";
import { useChatStore } from "../store/chat-store";
import { useChatAccessStore } from "../store/chat-access-store";
import { CHAT_MAX_ATTACHMENTS } from "../constants";
import type { ChatAttachment } from "../types";

const isChatPage = (pathname: string) => /^\/chat\/[^/]+/.test(pathname);

const getChatIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/chat\/([^/]+)/);
  return match?.[1] ?? null;
};

/** Sayfanın kendi bırakma alanı — sohbet katmanı bunu çalmaz */
const LOCAL_FILE_DROP = "[data-local-file-drop]";

const isLocalFileDropTarget = (event: DragEvent): boolean => {
  const target = event.target;
  return target instanceof Element && Boolean(target.closest(LOCAL_FILE_DROP));
};

const isBareLayout = (matches: ReturnType<typeof useMatches>): boolean =>
  matches.some((match) => {
    const handle = match.handle;
    return (
      typeof handle === "object" &&
      handle !== null &&
      (handle as ModuleRouteHandle).layout === "bare"
    );
  });

/** Masaüstünden gelen dosya sürüklemesi — sayfa içi metin/görsel drag'ini eleme */
const isExternalFileDrag = (event: DragEvent): boolean => {
  const transfer = event.dataTransfer;
  if (!transfer) return false;

  const types = Array.from(transfer.types);
  if (!types.includes("Files")) return false;

  if (transfer.items && transfer.items.length > 0) {
    return Array.from(transfer.items).some((item) => item.kind === "file");
  }

  return true;
};

export const useChatFileDrop = () => {
  const { t } = useTranslation();
  const { error: toastError } = useToast();
  const location = useLocation();
  const matches = useMatches();
  const activeModule = useActiveModule();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const openAuthGate = useAuthGateStore((state) => state.openAuthGate);
  const { chatId: dockChatId, isOpen: isDockOpen, open: openDock } = useChatDock();

  const attachmentCount = useChatStore((state) => state.selectedAttachments.length);
  const addSelectedAttachments = useChatStore((state) => state.addSelectedAttachments);
  const isPickerOpen = useFilePickerStore((state) => state.isOpen);

  const onChatPage = isChatPage(location.pathname);
  const bare = isBareLayout(matches);
  const moduleChatEnabled = Boolean(
    activeModule && getModuleChatConfig(activeModule)?.enabled,
  );
  // Tam sayfa sohbet, ya da sohbeti açık bir modülün kabuğu (dock).
  // Stüdyo gibi bare sayfalar kendi bırakma alanını kullanır.
  const isModuleSurface = moduleChatEnabled && !bare && !onChatPage;
  const isComposerSurface = onChatPage || isModuleSurface;

  const activeChatId = onChatPage
    ? getChatIdFromPath(location.pathname)
    : dockChatId;

  const canEdit = useChatAccessStore((state) =>
    activeChatId ? state.byChatId[activeChatId]?.canEdit : undefined,
  );

  const isReadOnlyChat = canEdit === false;
  const isEnabled = isComposerSurface && !isPickerOpen && !isReadOnlyChat;

  const remainingSlots = Math.max(0, CHAT_MAX_ATTACHMENTS - attachmentCount);

  const [isDragging, setIsDragging] = useState(false);
  const [hoveringLocalDrop, setHoveringLocalDrop] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dragCounterRef = useRef(0);

  const { uploadSingleFile } = useFileUpload({ isInput: true });

  const resetDrag = useCallback(() => {
    dragCounterRef.current = 0;
    setIsDragging(false);
    setHoveringLocalDrop(false);
  }, []);

  const handleDroppedFiles = useCallback(
    async (fileList: FileList) => {
      if (!isAuthenticated) {
        openAuthGate({ description: t("commerceLanding.signInToAsk") });
        return;
      }

      const files = Array.from(fileList);
      if (files.length === 0) return;

      const currentCount = useChatStore.getState().selectedAttachments.length;
      const remaining = CHAT_MAX_ATTACHMENTS - currentCount;

      if (remaining <= 0) {
        toastError(t("chat.dropOverlay.maxReached"));
        return;
      }

      const accepted: File[] = [];
      for (const file of files) {
        const validation = validateFile(file);
        if (!validation.valid) {
          toastError(t("chat.dropOverlay.unsupported"));
          continue;
        }
        accepted.push(file);
      }

      if (accepted.length === 0) return;

      const filesToUpload = accepted.slice(0, remaining);
      if (accepted.length > remaining) {
        toastError(t("chat.dropOverlay.maxReached"));
      }

      if (isModuleSurface && !isDockOpen) {
        openDock();
      }

      setIsProcessing(true);
      try {
        const attachments: ChatAttachment[] = [];

        for (const file of filesToUpload) {
          const response = await uploadSingleFile(file);
          if (!response) {
            toastError(t("chat.dropOverlay.uploadFailed"));
            continue;
          }

          const fullUrl = getFileUrl(response.data.file);
          if (!fullUrl) {
            toastError(t("chat.dropOverlay.uploadFailed"));
            continue;
          }

          attachments.push({
            id: response.data._id,
            url: fullUrl,
            name: file.name,
            mimeType: file.type,
            size: file.size,
          });
        }

        if (attachments.length > 0) {
          addSelectedAttachments(attachments);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [
      addSelectedAttachments,
      isAuthenticated,
      isDockOpen,
      isModuleSurface,
      openAuthGate,
      openDock,
      t,
      toastError,
      uploadSingleFile,
    ],
  );

  const surfaceRef = useRef({
    isComposerSurface,
    isPickerOpen,
    isReadOnlyChat,
  });
  surfaceRef.current = { isComposerSurface, isPickerOpen, isReadOnlyChat };

  const dropHandlerRef = useRef(handleDroppedFiles);
  dropHandlerRef.current = handleDroppedFiles;

  useEffect(() => {
    if (!isComposerSurface || isPickerOpen) {
      resetDrag();
      return;
    }

    const onDragEnter = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      event.preventDefault();
      if (surfaceRef.current.isReadOnlyChat) return;
      if (isLocalFileDropTarget(event)) {
        setHoveringLocalDrop(true);
        return;
      }
      dragCounterRef.current += 1;
      setIsDragging(true);
      setHoveringLocalDrop(false);
    };

    const onDragOver = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      const local = isLocalFileDropTarget(event);
      setHoveringLocalDrop((current) => (current === local ? current : local));
      if (local) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = surfaceRef.current.isReadOnlyChat
          ? "none"
          : "copy";
      }
    };

    const onDragLeave = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      if (isLocalFileDropTarget(event)) {
        setHoveringLocalDrop(false);
        return;
      }
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    const onDrop = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return;
      if (isLocalFileDropTarget(event)) {
        resetDrag();
        return;
      }
      event.preventDefault();
      resetDrag();

      if (surfaceRef.current.isReadOnlyChat || !event.dataTransfer?.files?.length) {
        return;
      }
      void dropHandlerRef.current(event.dataTransfer.files);
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver, true);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop, true);

    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver, true);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop, true);
      resetDrag();
    };
  }, [isComposerSurface, isPickerOpen, resetDrag]);

  const isVisible = useMemo(
    () => (isDragging && !hoveringLocalDrop) || isProcessing,
    [hoveringLocalDrop, isDragging, isProcessing],
  );

  return {
    isVisible,
    isProcessing,
    remainingSlots,
    isEnabled,
  };
};
